/* ==========================================================================
   custom-pack-picker.js  —  Volume theme
   --------------------------------------------------------------------------
   <pack-color-picker> custom element.

   Responsibilities:
     • Toggle 1 unit / 2-pack  -> shows/hides the 2nd color group
     • On color change: update that group's thumbnail + drive the theme's
       native Color radio so the MAIN gallery swaps using the theme's own code
     • Read the currently selected SIZE from your native size control
     • On add-to-cart: build 1 or 2 line items and POST /cart/add.js once,
       then refresh + open the cart drawer

   Two theme-specific hooks are centralised in CONFIG below. Defaults follow
   Volume/Dawn conventions; adjust if your theme differs.
   ========================================================================== */
(function () {
  'use strict';

  /* ----- theme-specific config (safe defaults, override if needed) ------- */
  var CONFIG = {
    // Section id used by the Section Rendering API to re-render the drawer.
    // Volume/Dawn ship a section file named "cart-drawer".
    cartDrawerSection: 'cart-drawer',
    // Selectors we try (in order) to OPEN the drawer after adding.
    cartDrawerSelector: 'cart-drawer',
    cartIconToggle: '#cart-icon-bubble, .header__icon--cart, [href$="/cart"]'
  };

  if (customElements.get('pack-color-picker')) return;

  customElements.define('pack-color-picker', class extends HTMLElement {
    connectedCallback() {
      this.sectionId   = this.dataset.sectionId;
      this.formId      = this.dataset.formId;
      this.colorIndex  = this.dataset.colorIndex === '' ? null : parseInt(this.dataset.colorIndex, 10);
      this.sizeIndex   = this.dataset.sizeIndex  === '' ? null : parseInt(this.dataset.sizeIndex, 10);
      this.colorName   = this.dataset.colorName || 'Color';

      // Parse the variant lookup map emitted by Liquid.
      try {
        this.variants = JSON.parse(this.querySelector('[data-pcp-variants]').textContent);
      } catch (e) { this.variants = []; }

      this.sectionEl = this.closest('.shopify-section') || document;
      this.errorEl   = this.querySelector('[data-pcp-error]');
      this.atcButton = this.querySelector('[data-pcp-atc]');
      this.priceEl   = this.querySelector('[data-pcp-atc-price]');
      this.packEach  = parseInt(this.dataset.packEach, 10) || 0;
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.pack      = 1;
      this.lastDrivenGroup = 0;

      this.hideNativePicker();
      this.bindPacks();
      this.bindColors();
      this.bindSize();
      this.bindButton();

      // Initialise from the pre-checked inputs.
      this.setPack(1);
      this.querySelectorAll('[data-pcp-group]').forEach(function (grp) {
        var checked = grp.querySelector('input:checked');
        if (checked) this.applyColor(grp, checked, /*drive*/ grp.dataset.pcpGroup === '0');
      }, this);
      this.refreshSizeAvailability();
      this.updateButtonPrice();
    }

    /* --- hide the theme's native variant picker; we drive it programmatically ---
       It stays in the DOM (display:none) so driving its Color radio still fires
       the theme's own gallery-switching code. Our block renders the visible UI. */
    hideNativePicker() {
      var vp = this.sectionEl.querySelector('variant-picker');
      if (vp) {
        var wrap = vp.closest('.variants') || vp;
        wrap.classList.add('pcp-native-hidden');
        return;
      }
      // Fallback: no <variant-picker> — hide just the native color group(s).
      var inputs = this.sectionEl.querySelectorAll('input[name="' + CSS.escape(this.colorName) + '"]');
      inputs.forEach(function (inp) {
        var w = inp.closest('.product-form__input');
        if (w) w.classList.add('pcp-native-hidden');
      });
    }

    /* ------------------------------- packs -------------------------------- */
    bindPacks() {
      var self = this;
      this.querySelectorAll('[data-pcp-pack]').forEach(function (card) {
        card.addEventListener('click', function () {
          var val = parseInt(card.dataset.pcpPack, 10);
          card.querySelector('input').checked = true;
          self.setPack(val);
        });
      });
    }

    setPack(val) {
      this.pack = val;
      this.querySelectorAll('[data-pcp-pack]').forEach(function (c) {
        c.classList.toggle('is-selected', parseInt(c.dataset.pcpPack, 10) === val);
      });
      var group2 = this.querySelector('[data-pcp-group="1"]');
      if (group2) group2.hidden = val !== 2;
      this.updatePackLabel();
      this.refreshSizeAvailability();
      this.updateButtonPrice();
      this.clearError();
    }

    updatePackLabel() {
      var el = this.querySelector('[data-pcp-pack-label]');
      if (!el) return;
      var active = this.querySelector('[data-pcp-pack="' + this.pack + '"] .pcp__pack-name');
      el.textContent = active ? active.textContent.trim() : '';
      // Update "Color:" summary line to reflect selected color(s).
      var summary = this.querySelector('[data-pcp-color-summary]');
      if (summary) {
        var names = [];
        var groups = this.pack === 2 ? [0, 1] : [0];
        groups.forEach(function (g) {
          var v = this.querySelector('[data-pcp-group="' + g + '"] input:checked');
          if (v) names.push(v.value);
        }, this);
        summary.textContent = names.join(', ');
      }
    }

    /* ------------------------------ colors -------------------------------- */
    bindColors() {
      var self = this;
      this.querySelectorAll('[data-pcp-group]').forEach(function (grp) {
        grp.addEventListener('change', function (e) {
          if (e.target.type !== 'radio') return;
          // Group 0 always drives the main gallery. Group 1 drives it too when
          // the user actively interacts with it (matches the reference UI).
          var drive = grp.dataset.pcpGroup === '0' || true;
          self.applyColor(grp, e.target, drive);
          self.lastDrivenGroup = parseInt(grp.dataset.pcpGroup, 10);
          self.refreshSizeAvailability();
          self.clearError();
        });
      });
    }

    applyColor(grp, input, drive) {
      // 1) update this group's own thumbnail + heading
      var thumb = grp.querySelector('[data-pcp-thumb]');
      if (thumb && input.dataset.thumb) { thumb.src = input.dataset.thumb; thumb.alt = input.value; }
      var name = grp.querySelector('[data-pcp-group-name]');
      if (name) name.textContent = input.value;
      // reflect selected state on the dot
      grp.querySelectorAll('.pcp__swatch').forEach(function (s) {
        s.classList.toggle('is-selected', s.contains(input));
      });
      // 2) drive the theme's native Color radio -> theme swaps the main gallery
      if (drive) this.driveNativeColor(input.value);
      this.updatePackLabel();
      this.updateButtonPrice();
    }

    driveNativeColor(colorValue) {
      var native = this.sectionEl.querySelector(
        'input[name="' + CSS.escape(this.colorName) + '"][value="' + CSS.escape(colorValue) + '"]'
      );
      if (native) {
        native.checked = true;
        native.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      // Fallback A: native color dropdown
      var sel = this.sectionEl.querySelector('select[name="options[' + this.colorName + ']"]');
      if (sel) {
        sel.value = colorValue;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      // Fallback B: no native picker at all — swap the gallery ourselves by media id.
      var input = this.querySelector('[data-pcp-group="0"] input[value="' + CSS.escape(colorValue) + '"]');
      if (input && input.dataset.media) this.swapGalleryByMedia(input.dataset.media);
    }

    /* Best-effort gallery swap when there's no native picker to drive. */
    swapGalleryByMedia(mediaId) {
      var gallery = this.sectionEl.querySelector('media-gallery, [id^="MediaGallery"], .product__media-list');
      if (!gallery) return;
      // Dawn/Volume media-gallery exposes setActiveMedia(sectionId-mediaId).
      if (typeof gallery.setActiveMedia === 'function') {
        try { gallery.setActiveMedia(this.sectionId + '-' + mediaId, true); return; } catch (e) {}
      }
      // Generic fallback: mark the matching slide active and scroll to it.
      var target = gallery.querySelector('[data-media-id$="' + mediaId + '"], [data-media-id="' + mediaId + '"]');
      if (target) {
        gallery.querySelectorAll('.is-active').forEach(function (n) { n.classList.remove('is-active'); });
        target.classList.add('is-active');
        target.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      }
    }

    /* ------------------------------- size --------------------------------- */
    bindSize() {
      var self = this;
      this.querySelectorAll('[data-pcp-size] input[type="radio"]').forEach(function (r) {
        r.addEventListener('change', function () {
          var name = self.querySelector('[data-pcp-size-name]');
          if (name) name.textContent = r.value;
          self.querySelectorAll('.pcp__size-pill').forEach(function (p) {
            p.classList.toggle('is-selected', p.contains(r));
          });
          self.updateButtonPrice();
          self.clearError();
        });
      });
      // reflect the initially-checked pill
      var checked = this.querySelector('[data-pcp-size] input:checked');
      if (checked) checked.closest('.pcp__size-pill').classList.add('is-selected');
    }

    getSelectedSize() {
      if (this.sizeIndex === null) return null; // product has no Size option
      // Our own size pills first…
      var own = this.querySelector('[data-pcp-size] input:checked');
      if (own) return own.value;
      // …then any native control (in case the block is rendered without its size).
      var sel = this.sectionEl.querySelector(
        'select[name^="options[Size"], select[id*="-Size-"], select[name="options[Size]"]'
      );
      if (sel && sel.value) return sel.value;
      var r = this.sectionEl.querySelector('input[name="Size"]:checked, input[name="size"]:checked');
      if (r) return r.value;
      return null;
    }

    /* Grey out sizes not available for ALL currently-selected colors. */
    refreshSizeAvailability() {
      var pills = this.querySelectorAll('[data-pcp-size] .pcp__size-pill');
      if (!pills.length) return;
      var groups = this.pack === 2 ? [0, 1] : [0];
      var colors = [];
      groups.forEach(function (g) {
        var c = this.querySelector('[data-pcp-group="' + g + '"] input:checked');
        if (c) colors.push(c.value);
      }, this);

      var self = this;
      pills.forEach(function (pill) {
        var input = pill.querySelector('input');
        var sizeVal = input.value;
        var ok = colors.length > 0 && colors.every(function (color) {
          var v = self.findVariant(color, sizeVal);
          return v && v.available;
        });
        pill.classList.toggle('pcp__size-pill--soldout', !ok);
        input.disabled = !ok;
        // If the checked size just became unavailable, move to the first available one.
        if (!ok && input.checked) {
          input.checked = false;
          var next = self.querySelector('[data-pcp-size] .pcp__size-pill:not(.pcp__size-pill--soldout) input');
          if (next) { next.checked = true; next.dispatchEvent(new Event('change', { bubbles: true })); }
        }
      });
    }

    /* ------------------------- variant resolution ------------------------- */
    findVariant(color, size) {
      for (var i = 0; i < this.variants.length; i++) {
        var v = this.variants[i];
        var colorOk = this.colorIndex === null || v.options[this.colorIndex] === color;
        var sizeOk  = this.sizeIndex  === null || v.options[this.sizeIndex]  === size;
        if (colorOk && sizeOk) return v;
      }
      return null;
    }

    /* ------------------------- build cart line items ---------------------- */
    buildItems() {
      var size = this.getSelectedSize();
      if (this.sizeIndex !== null && !size) {
        return { error: 'Please select a size.' };
      }

      var groups = this.pack === 2 ? [0, 1] : [0];
      var chosen = [];
      for (var g = 0; g < groups.length; g++) {
        var input = this.querySelector('[data-pcp-group="' + groups[g] + '"] input:checked');
        if (!input) return { error: 'Please choose a color.' };
        var v = this.findVariant(input.value, size);
        if (!v) return { error: 'That combination is unavailable. Please pick another option.' };
        if (!v.available) return { error: '"' + input.value + '" in size ' + (size || '') + ' is sold out.' };
        chosen.push(v.id);
      }

      // Merge duplicate variants (e.g. 2-pack, same color) into quantity.
      var counts = {};
      chosen.forEach(function (id) { counts[id] = (counts[id] || 0) + 1; });
      var items = Object.keys(counts).map(function (id) {
        return { id: parseInt(id, 10), quantity: counts[id] };
      });
      return { items: items };
    }

    /* --------------------------- add-to-bag button ------------------------ */
    bindButton() {
      var self = this;
      if (!this.atcButton) return;
      this.atcButton.addEventListener('click', function () { self.addToCart(); });
    }

    setLoading(on) {
      if (!this.atcButton) return;
      this.atcButton.classList.toggle('is-loading', on);
      this.atcButton.setAttribute('aria-busy', on ? 'true' : 'false');
      this.atcButton.disabled = on;
    }

    /* Live total shown on the button. */
    updateButtonPrice() {
      if (!this.priceEl) return;
      var total;
      if (this.pack === 2) {
        total = this.packEach * 2;              // advertised 2-pack total
      } else {
        var input = this.querySelector('[data-pcp-group="0"] input:checked');
        var v = input ? this.findVariant(input.value, this.getSelectedSize()) : null;
        total = v ? v.price : this.firstPrice();
      }
      if (total != null) this.priceEl.innerHTML = this.formatMoney(total);
    }

    firstPrice() { return this.variants.length ? this.variants[0].price : null; }

    /* Standard Shopify money formatter (supports the common format tokens). */
    formatMoney(cents) {
      var format = this.moneyFormat;
      var placeholder = /\{\{\s*(\w+)\s*\}\}/;
      function withDelimiters(number, precision, thousands, decimal) {
        precision = precision == null ? 2 : precision;
        thousands = thousands == null ? ',' : thousands;
        decimal   = decimal   == null ? '.' : decimal;
        if (isNaN(number) || number == null) return '0';
        number = (number / 100).toFixed(precision);
        var parts = number.split('.');
        var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        var cents = parts[1] ? decimal + parts[1] : '';
        return dollars + cents;
      }
      var token = (format.match(placeholder) || [null, 'amount'])[1];
      var value;
      switch (token) {
        case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
        case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
        case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
        default: value = withDelimiters(cents, 2);
      }
      return format.replace(placeholder, value);
    }

    addToCart() {
      var built = this.buildItems();
      if (built.error) { this.showError(built.error); return; }
      this.clearError();
      this.setLoading(true);

      var self = this;
      var drawer = this.getDrawer();

      // Ask for exactly the sections the theme's own drawer wants to re-render.
      var sections = [CONFIG.cartDrawerSection];
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        try {
          sections = drawer.getSectionsToRender().map(function (s) { return s.id; });
        } catch (e) {}
      }

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: built.items,
          sections: sections,
          sections_url: window.location.pathname
        })
      })
        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
        .then(function (res) {
          if (!res.ok) {
            self.showError(res.data && res.data.description ? res.data.description : 'Could not add to cart.');
            return;
          }
          self.openOrRenderDrawer(drawer, res.data);
          self.notifyCartUpdate(res.data);
        })
        .catch(function () { self.showError('Something went wrong. Please try again.'); })
        .finally(function () { self.setLoading(false); });
    }

    /* --------------------------- cart drawer ------------------------------ */
    getDrawer() {
      return document.querySelector(CONFIG.cartDrawerSelector) ||
             document.querySelector('cart-drawer') ||
             document.querySelector('cart-notification');
    }

    openOrRenderDrawer(drawer, data) {
      if (!drawer) {
        console.warn('[pack-picker] No cart drawer element found. Set CONFIG.cartDrawerSelector to your drawer\'s tag/selector.');
      } else if (typeof drawer.renderContents !== 'function' && typeof drawer.open !== 'function') {
        console.warn('[pack-picker] Cart drawer <' + drawer.tagName.toLowerCase() + '> has no renderContents()/open(); using markup fallback.');
      }
      // 1) Preferred: the theme's own custom element renders AND opens itself.
      //    (Dawn/Volume `cart-drawer.renderContents()` fills sections then opens.)
      if (drawer && typeof drawer.renderContents === 'function') {
        if (drawer.classList) drawer.classList.remove('is-empty');
        try { drawer.renderContents(data); return; } catch (e) {}
      }

      // 2) Manually replace the drawer's inner section markup, then open.
      var updated = this.replaceDrawerSections(drawer, data.sections);

      // 3) Open via whatever hook the theme exposes.
      if (drawer && typeof drawer.open === 'function') { try { drawer.open(); return; } catch (e) {} }

      // 4) Class/attribute fallbacks used by common themes + lock body scroll.
      if (drawer && drawer.classList) {
        drawer.classList.add('active', 'animate', 'is-open', 'open', 'drawer--active');
        drawer.setAttribute('open', '');
        drawer.setAttribute('aria-hidden', 'false');
      }
      document.body.classList.add('overflow-hidden', 'cart-drawer-open', 'js-drawer-open');
      document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));

      // 5) Last resort: click the header cart link to trigger the theme's own opener.
      if (!updated || !(drawer && drawer.classList && drawer.classList.contains('active'))) {
        var toggle = document.querySelector(CONFIG.cartIconToggle);
        if (toggle) toggle.click();
      }
    }

    /* Replace each returned section's markup inside the drawer element. */
    replaceDrawerSections(drawer, sections) {
      if (!drawer || !sections) return false;
      var did = false;
      Object.keys(sections).forEach(function (key) {
        var doc = new DOMParser().parseFromString(sections[key], 'text/html');
        // Try to graft the freshly rendered inner content over the live one.
        var candidates = ['.drawer__inner', '#CartDrawer', '.cart-drawer__inner', '.cart__contents'];
        for (var i = 0; i < candidates.length; i++) {
          var fresh = doc.querySelector(candidates[i]);
          var live = drawer.querySelector(candidates[i]);
          if (fresh && live) { live.innerHTML = fresh.innerHTML; did = true; break; }
        }
        if (!did) {
          // Fall back to replacing the drawer's own inner HTML with the section body.
          var freshRoot = doc.querySelector(CONFIG.cartDrawerSelector) || doc.body;
          if (freshRoot) { drawer.innerHTML = freshRoot.innerHTML; did = true; }
        }
      });
      if (did && drawer.classList) drawer.classList.remove('is-empty');
      return did;
    }

    notifyCartUpdate(cart) {
      // Let the theme's header/count update if it uses a PubSub bus.
      try {
        if (window.publish && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'pack-color-picker', cartData: cart });
        }
      } catch (e) {}
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }

    /* ------------------------------ errors -------------------------------- */
    showError(msg) { if (this.errorEl) { this.errorEl.textContent = msg; this.errorEl.hidden = false; } }
    clearError()   { if (this.errorEl) { this.errorEl.hidden = true; this.errorEl.textContent = ''; } }
  });
})();